import React, { ChangeEvent, FC, useCallback, useMemo } from 'react';
import {
  ListItemText,
  ListItemTextProps,
  MenuItem,
  SxProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { useSelectedOrganizationName } from '@client/store/organization';
import amplitudeLog from '@client/amplitude';
import { AMP_APP_ORGANIZATION_SELECT } from '@shared/constants';
import { sortBy } from 'lodash';
import { UserOrganization } from '@shared/models/user.model';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';

interface OrganizationsMenuProps {
  organizations: UserOrganization[];
}

const OrganizationsMenu: FC<OrganizationsMenuProps> = ({ organizations }) => {
  const navigate = useProjectNavigation();
  const selectedOrganization = useSelectedOrganizationName();

  const sortedOrganizations = useMemo(
    () => sortBy(organizations, (org) => org.display_name.toLocaleLowerCase()),
    [organizations],
  );

  const handleMenuChange = useCallback(
    ({
      target: { value },
    }: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
      amplitudeLog(AMP_APP_ORGANIZATION_SELECT, {
        setOrganization: value,
      });
      navigate({ organization: value });
    },
    [navigate],
  );

  return (
    organizations.length > 1 && (
      <TextField
        select
        size="small"
        placeholder="Select organization"
        fullWidth={false}
        sx={textFieldSx}
        slotProps={textFieldSlotProps}
        value={selectedOrganization || ''}
        onChange={handleMenuChange}
      >
        {sortedOrganizations.map(({ id, name, display_name }) => (
          <MenuItem key={id} value={name}>
            <ListItemText
              primary={display_name}
              slotProps={listItemTextSlotProps}
            />
          </MenuItem>
        ))}
      </TextField>
    )
  );
};

const textFieldSx: SxProps = {
  minWidth: 120,
  '& .MuiInputBase-root': {
    fontSize: 11,
    height: 24,
  },
  '& .MuiSelect-select': {
    padding: '4px 24px 4px 8px',
  },
};
const textFieldSlotProps: TextFieldProps['slotProps'] = {
  input: { sx: { height: 24 } },
};
const listItemTextSlotProps: ListItemTextProps['slotProps'] = {
  primary: { variant: 'body2' },
};

export default OrganizationsMenu;
